import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { listRecentSales } from "../api/atomicApi";
import { formatAssetName } from "../utils/backing";
import { formatRelativeTime } from "../utils/time";
import type { MarketSale } from "../types";

const ITEM_COUNT = 20; // how many recent sales feed the ticker
const REFRESH_MS = 30000; // same cadence as the Recently Sold page — one shared feed, no extra API pressure
const TICK_MS = 5000; // re-render "X ago" labels without waiting on a full refetch
const SECONDS_PER_ITEM = 3.5; // scroll speed: more items → proportionally longer loop, same pace

function saleTimeMs(sale: MarketSale): number {
  const raw = sale.updated_at_time;
  const n = raw ? Number(raw) : NaN;
  return Number.isFinite(n) ? n : 0;
}

function formatPrice(sale: MarketSale): string {
  return `${(Number(sale.price.amount) / 10 ** sale.price.token_precision).toFixed(sale.price.token_precision)} ${
    sale.price.token_symbol
  }`;
}

/**
 * Site-wide "recently sold" marquee, styled after a stock-market ticker —
 * fixed to the bottom of the viewport (see .sales-ticker in global.css and
 * App.tsx, which renders this once outside the page router so it persists
 * across navigation) and scrolling continuously. Pulls the same
 * listRecentSales feed as the Recently Sold page, just trimmed to a
 * smaller, constantly-refreshing slice.
 *
 * Decorative, not a primary navigation surface: everything shown here is
 * also reachable, in full and screen-reader-friendly, from the Recently
 * Sold page. So the scrolling strip itself is aria-hidden and the items
 * are duplicated in the DOM (back-to-back) purely to make the CSS
 * translateX loop visually seamless — a screen reader announcing each
 * duplicate row would be noise, not information.
 */
export function SalesTicker() {
  const [sales, setSales] = useState<MarketSale[]>([]);
  const [, setTick] = useState(0); // forces "X ago" labels to stay fresh

  useEffect(() => {
    let cancelled = false;

    function load() {
      listRecentSales(ITEM_COUNT)
        .then((data) => {
          if (!cancelled) setSales(data);
        })
        .catch(() => {
          /* best-effort — the ticker just keeps showing the last good list */
        });
    }

    load();
    const refreshId = window.setInterval(load, REFRESH_MS);
    const tickId = window.setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => {
      cancelled = true;
      window.clearInterval(refreshId);
      window.clearInterval(tickId);
    };
  }, []);

  if (sales.length === 0) return null;

  const duration = Math.max(20, Math.round(sales.length * SECONDS_PER_ITEM));

  // Rendered twice back-to-back so the 0%→-50% loop lines up seamlessly.
  const row = (copyKey: string) => (
    <div className="sales-ticker__row" key={copyKey}>
      {sales.map((sale) => {
        const asset = sale.assets[0];
        return (
          <Link
            key={`${copyKey}-${sale.sale_id}`}
            to={asset ? `/asset/${asset.asset_id}` : "/recently-sold"}
            className="sales-ticker__item"
            tabIndex={-1}
          >
            <span className="sales-ticker__dot" />
            <span className="sales-ticker__label">SOLD</span>
            <span className="sales-ticker__name">{asset ? formatAssetName(asset) : `Sale #${sale.sale_id}`}</span>
            <span className="sales-ticker__price">{formatPrice(sale)}</span>
            <span className="sales-ticker__time">{formatRelativeTime(saleTimeMs(sale))}</span>
          </Link>
        );
      })}
    </div>
  );

  return (
    <div className="sales-ticker">
      <Link to="/recently-sold" className="sales-ticker__badge">
        🔴 LIVE SALES
      </Link>
      <div className="sales-ticker__viewport" aria-hidden="true">
        <div className="sales-ticker__track" style={{ animationDuration: `${duration}s` }}>
          {row("a")}
          {row("b")}
        </div>
      </div>
    </div>
  );
}
